import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import liff from '@line/liff';
import HeaderHero from './templates/HeaderHero';
import closedLogo from '../../assets/images/game-bts/Logo-Bill-Roojai-bts.png';
import SummaryCard from './templates/SummaryCard';
import TabNavigation from './templates/TabNavigation';
import MissionsPage from './pages/MissionsPage';
import TeamPage from './pages/TeamPage';
import HistoryPage from './pages/HistoryPage';
import RankingPage from './pages/RankingPage';
// import FixedBottomCTA from './templates/FixedBottomCTA';
import ScanPage from './pages/ScanPage';
import BtsPopup from './templates/BtsPopup';
import BtsHowToPlay from './templates/BtsHowToPlay';
import { btsApi } from './services/btsApi';
import { btsShareHelper } from './services/btsShareHelper';
import './GameBtsApp.css';
const IS_PROD = process.env.REACT_APP_ENV === 'prod';
const log = (...args) => { if (!IS_PROD) console.log(...args); };
const LIFF_ID = process.env.REACT_APP_LIFF_ID;
const SHARE_BASE_URL = `https://liff.line.me/${LIFF_ID}`;
// Campaign end at end-of-day 27 May 2026 (= 00:00 28 May Bangkok)
const CAMPAIGN_END = new Date('2026-05-28T00:00:00+07:00');
const daysLeftUntilEnd = () => Math.max(0, Math.floor((CAMPAIGN_END.getTime() - Date.now()) / 86400000));
const getCountdownText = () => {
  const dLeft = daysLeftUntilEnd();
  if (dLeft === 0) return `⏰ วันนี้เป็นวันสุดท้าย! รีบสะสมก่อนปิดแคมเปญ`;
  if (dLeft === 1) return `⏰ พรุ่งนี้เป็นวันสุดท้าย! รีบสะสมก่อนปิดแคมเปญ`;
  return `⏰ อีก ${dLeft} วัน เกมจะจบ! รีบสะสมก่อนปิดแคมเปญ`;
};
// delay popup หลังเพิ่งทำ action (scan/join) — กัน popup เด้งทันที
const POPUP_DELAY_MS = 90_000;
const isRecentAction = () => {
  const keys = ['bts_came_from_scan', 'bts_just_joined'];
  return keys.some(k => {
    const ts = parseInt(sessionStorage.getItem(k) || '0', 10);
    return ts && (Date.now() - ts) < POPUP_DELAY_MS;
  });
};
const isCampaignEnded = () => Date.now() >= CAMPAIGN_END.getTime();

const GameBtsApp = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const goto = sessionStorage.getItem('bts_goto_tab');
    if (goto) { sessionStorage.removeItem('bts_goto_tab'); return goto; }
    return 'missions';
  });
  const [scanOpen, setScanOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [popup, setPopup] = useState(null);
  const [joiningParty, setJoiningParty] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [campaignEnded, setCampaignEnded] = useState(isCampaignEnded());

  // เช็คเที่ยงคืนทุกนาที — ถ้าเลย CAMPAIGN_END แล้ว → block ทั้งเกม
  useEffect(() => {
    if (campaignEnded) return;
    const check = () => { if (isCampaignEnded()) setCampaignEnded(true); };
    const id = setInterval(check, 30_000);
    return () => clearInterval(id);
  }, [campaignEnded]);

  const showPopup = (title, message, type = 'error', actions) => {
    setPopup({ title, message, type, actions });
  };

  // ดึงรูป LINE profile เอง ถ้ายังไม่มีใน localStorage
  useEffect(() => {
    const syncLineProfile = async () => {
      try {
        if (localStorage.getItem('line_picture_url')) return; // มีแล้ว ไม่ต้องดึง
        if (!liff.isLoggedIn()) return;
        const profile = await liff.getProfile();
        if (profile.pictureUrl) localStorage.setItem('line_picture_url', profile.pictureUrl);
        if (profile.displayName) localStorage.setItem('line_display_name', profile.displayName);
        log('[BTS] Synced LINE profile picture:', profile.pictureUrl);
      } catch (e) {
        log('[BTS] Failed to sync LINE profile:', e);
      }
    };
    syncLineProfile();
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      log('[BTS] Fetching dashboard...');
      await btsApi.initPlayer();
      const res = await btsApi.getDashboard();
      log('[BTS] Dashboard data:', res);
      setDashboard(res?.data || null);
    } catch (err) {
      console.error('[BTS] Dashboard error:', err);
      const combined = `${err?.data?.error || ''} ${err?.message || ''}`;
      if (combined.includes('EMPLOYEE_BLOCKED')) {
        setPopup(prev => prev || {
          title: 'ไม่สามารถร่วมกิจกรรมได้',
          message: 'พนักงานไม่สามารถร่วมเล่นเกมได้',
          type: 'error',
          actions: [{ label: 'ตกลง', primary: true, onClick: () => { window.location.href = '/'; } }],
        });
      } else if (combined.includes('NO_BIGPOINT') || combined.includes('PLAYER_NOT_FOUND')) {
        setPopup(prev => prev || {
          title: 'ยังไม่ได้เป็นสมาชิก BigPoint',
          message: 'ต้องสมัครสมาชิก BigPoint ก่อนถึงจะเข้าร่วมเกมได้',
          type: 'warning',
          actions: [{ label: 'ไปลงทะเบียน', primary: true, onClick: () => {
            sessionStorage.setItem('bts_redirect_after_register', '/game/bts');
            window.location.href = '/register';
          } }],
        });
      }
      setDashboard(null);
    } finally {
      setDashLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // แสดง howto ครั้งเดียว ถ้ายังไม่มีตี้ + ยังไม่เคยกด accept + ไม่มี invite + ไม่กำลัง join
  useEffect(() => {
    if (dashLoading || joiningParty || popup) return;
    if (campaignEnded) return; // เกมจบแล้ว → ไม่เด้ง howto
    const hasInvite = searchParams.get('invite') || sessionStorage.getItem('bts_invite_code');
    if (!dashboard?.party && !sessionStorage.getItem('bts_howto_seen') && !hasInvite) {
      setShowHowTo(true);
    }
  }, [dashLoading, dashboard, searchParams, joiningParty, popup, campaignEnded]);

  const handleAcceptHowTo = () => {
    sessionStorage.setItem('bts_howto_seen', '1');
    setShowHowTo(false);
  };

  // =====================================================
  // Popup เตือนคนมี pending bill แต่ยังไม่มีทีม — 1 ครั้ง/วัน
  // ⚠️ Skip ถ้าเพิ่งสแกนเสร็จ (กัน popup เด้งกระแทก flow scan)
  // =====================================================
  useEffect(() => {
    if (dashLoading || joiningParty || popup || showHowTo) return;
    // เคยผ่าน scan flow ใน session นี้ → skip popup ทั้งหมด (ครอบคลุมทั้ง scan ในเกม + ข้างนอก)
    // flag ค้างจนกว่าจะปิด tab / เปิด session ใหม่
    if (campaignEnded) return;
    if (isRecentAction()) return;
    if (dashboard?.party) return; // มีทีมแล้ว → ไม่แสดง
    const pending = dashboard?.pendingCount || 0;
    if (pending <= 0) return;

    const todayKey = `bts_pending_reminder_${new Date().toISOString().split('T')[0]}`;
    if (localStorage.getItem(todayKey)) return;
    localStorage.setItem(todayKey, '1'); // set ทันที — กดอะไรก็ไม่เด้งซ้ำวันนี้

    setPopup({
      title: '🎟️ บิลของคุณกำลังรอสะสมคะแนน',
      message: `คุณมีบิลรอสะสม ${pending} ใบ\nคุณต้องอยู่ในทีมก่อน บิลถึงจะเปลี่ยนเป็นคะแนน\nรีบสร้างหรือเข้าร่วมทีมเลย!\n\n${getCountdownText()}\n🍗 ลุ้นรางวัลกินไก่ฟรีทั้งปี!`,
      type: 'warning',
      actions: [
        { label: 'ไปสร้าง/เข้าทีม', primary: true, onClick: () => {
          setPopup(null);
          setActiveTab('team');
        }},
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashLoading, dashboard, joiningParty, showHowTo, campaignEnded]);

  // =====================================================
  // Popup เตือนชวนเพื่อน — 1 ครั้ง/วัน เฉพาะคนมีทีม + ยังไม่ครบ 4
  // ⚠️ Skip ถ้าเพิ่งสแกนเสร็จ
  // =====================================================
  useEffect(() => {
    if (dashLoading || joiningParty || popup || showHowTo) return;
    // เคยผ่าน scan flow ใน session นี้ → skip popup ทั้งหมด (ครอบคลุมทั้ง scan ในเกม + ข้างนอก)
    // flag ค้างจนกว่าจะปิด tab / เปิด session ใหม่
    if (campaignEnded) return;
    if (isRecentAction()) return;
    const party = dashboard?.party;
    if (!party || party.member_count >= 4) return;

    const todayKey = `bts_invite_reminder_${new Date().toISOString().split('T')[0]}`;
    if (localStorage.getItem(todayKey)) return;
    localStorage.setItem(todayKey, '1'); // set ทันที — กดอะไรก็ไม่เด้งซ้ำวันนี้

    const missing = 4 - party.member_count;
    const shareUrl = `${SHARE_BASE_URL}/game/bts?invite=${party.party_code}`;
    setPopup({
      title: '🏆 ทีมยังไม่ครบ',
      message: `ทีม "${party.party_name || 'ของคุณ'}" ยังขาดอีก ${missing} คน\nรีบชวนเพื่อนมาให้ครบ 4 คน\n\n${getCountdownText()}\n🍗 ลุ้นรางวัลกินไก่ฟรีทั้งปี!`,
      type: 'warning',
      actions: [
        { label: 'ชวนเพื่อน', primary: true, onClick: () => {
          setPopup(null);
          btsShareHelper.smartShare(party.party_code, shareUrl);
        }},
        { label: 'ปิด', onClick: () => setPopup(null) },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashLoading, dashboard, joiningParty, showHowTo, campaignEnded]);

  // =====================================================
  // Popup #3: ทีมครบ 4 คนแล้ว — เริ่มสะสมคะแนน — 1 ครั้ง/วัน
  // ⚠️ Skip ถ้าเพิ่งสแกน < 90 วินาที (ครอบคลุมทั้ง scan ในเกม + ข้างนอก)
  // =====================================================
  useEffect(() => {
    if (dashLoading || joiningParty || popup || showHowTo) return;
    if (campaignEnded) return;
    if (isRecentAction()) return;
    const party = dashboard?.party;
    if (!party || party.member_count < 4) return; // เฉพาะทีมครบ

    const todayKey = `bts_full_team_reminder_${new Date().toISOString().split('T')[0]}`;
    if (localStorage.getItem(todayKey)) return;
    localStorage.setItem(todayKey, '1');

    setPopup({
      title: '🎉 ทีมครบ 4 คนแล้ว!',
      message: `เริ่มสแกนใบเสร็จ Big C เก็บคะแนนได้เลย\n\n${getCountdownText()}\n🍗 ลุ้นรางวัลกินไก่ฟรีทั้งปี!`,
      type: 'success',
      actions: [
        { label: 'ตกลง', primary: true, onClick: () => setPopup(null) },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashLoading, dashboard, joiningParty, showHowTo, campaignEnded]);

  // =====================================================
  // Handle invite link: /game/bts?invite=PARTY_CODE
  // เก็บ invite code ใน sessionStorage ด้วย กันหายตอน redirect auth
  // =====================================================
  useEffect(() => {
    const inviteFromUrl = searchParams.get('invite');
    if (inviteFromUrl) {
      sessionStorage.setItem('bts_invite_code', inviteFromUrl);
      log('[BTS] Saved invite code to session:', inviteFromUrl);
    }

    const inviteCode = inviteFromUrl || sessionStorage.getItem('bts_invite_code');
    if (!inviteCode || joiningParty || dashLoading) return;
    // เกมปิดแล้ว → ไม่ยิง join API
    if (campaignEnded) {
      sessionStorage.removeItem('bts_invite_code');
      return;
    }

    const joinFromInvite = async () => {
      setJoiningParty(true);
      log('[BTS] Joining party from invite:', inviteCode);

      // helper: popup พร้อมปุ่มไปหน้าทีม
      const goTeam = () => { setPopup(null); setActiveTab('team'); };
      const teamAction = [{ label: 'ไปหน้าทีม', primary: true, onClick: goTeam }];

      try {
        await btsApi.initPlayer();
        const res = await btsApi.joinParty(inviteCode);
        log('[BTS] Join result:', res);
        sessionStorage.setItem('bts_just_joined', Date.now().toString());

        if (res?.data?.alreadyMember) {
          showPopup('เข้าร่วมแล้ว', 'คุณอยู่ในทีมนี้อยู่แล้ว', 'info', teamAction);
        } else if (res?.data?.isNewMember) {
          showPopup('เข้าร่วมสำเร็จ!', '🌟 ยินดีต้อนรับ New Member!\nคุณได้รับสิทธิ์คะแนน x2 ทุกใบเสร็จ!', 'success', teamAction);
        } else {
          showPopup('เข้าร่วมสำเร็จ!', 'ยินดีต้อนรับเข้าสู่ทีม! 🎉', 'success', teamAction);
        }
      } catch (err) {
        console.error('[BTS] Join from invite error:', err);
        const m = err?.message || '';
        const errorCode = err?.data?.error || '';
        const combined = `${errorCode} ${m}`;
        let title = 'เข้าร่วมไม่สำเร็จ';
        let msg = 'กรุณาลองใหม่';

        if (combined.includes('EMPLOYEE_BLOCKED')) {
          title = 'ไม่สามารถร่วมกิจกรรมได้';
          msg = 'พนักงานไม่สามารถร่วมเล่นเกมได้\nกรุณาติดตามกิจกรรมใหม่ในครั้งหน้า';
        } else if (combined.includes('NO_BIGPOINT') || combined.includes('NO_BIGPOINT_ACCOUNT')) {
          showPopup(
            'ยังไม่ได้เป็นสมาชิก BigPoint',
            'คุณยังไม่มีบัญชี BigPoint\nต้องสมัครสมาชิก BigPoint ก่อนถึงจะเข้าร่วมทีมได้',
            'warning',
            [{ label: 'ลงทะเบียน BigPoint', primary: true, onClick: () => {
              sessionStorage.setItem('bts_invite_code', inviteCode);
              window.location.href = '/register';
            }}]
          );
          return;
        } else if (combined.includes('ALREADY_IN_PARTY')) {
          title = 'คุณมีทีมอยู่แล้ว';
          msg = 'สมาชิก 1 คนอยู่ได้ 1 ทีมเท่านั้น';
        } else if (combined.includes('PARTY_NOT_FOUND')) {
          title = 'ไม่พบทีม';
          msg = 'ไม่พบทีมนี้ อาจถูกลบไปแล้ว';
        } else if (combined.includes('PARTY_FULL')) {
          title = 'ทีมเต็ม';
          msg = 'ทีมนี้มีสมาชิกครบ 4 คนแล้ว';
        } else if (combined.includes('PARTY_NOT_ACTIVE')) {
          title = 'ทีมปิดแล้ว';
          msg = 'ทีมนี้ไม่สามารถเข้าร่วมได้';
        } else if (combined.includes('CAMPAIGN_NOT_ACTIVE')) {
          title = 'นอกช่วงเวลา';
          msg = 'ขณะนี้อยู่นอกช่วงเวลาแคมเปญ';
        } else {
          title = err?.data?.title || title;
          msg = err?.data?.message || m || msg;
        }

        showPopup(title, msg, 'error', teamAction);
      } finally {
        setJoiningParty(false);
        sessionStorage.removeItem('bts_invite_code');
        // ลบ ?invite= ออกจาก URL กันวนลูป — ใช้ replaceState ตรงๆ ไม่ trigger React re-render
        window.history.replaceState({}, '', window.location.pathname);
      }
    };

    joinFromInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashLoading]);

  const handleScanClose = () => {
    setScanOpen(false);
    setActiveTab('team');
    fetchDashboard();
  };

  const user = {
    totalPoints: Math.floor(dashboard?.totalPoints || 0),
    billCount: dashboard?.billCount || 0,
    rank: (dashboard?.party?.member_count >= 4 && dashboard?.rank) ? dashboard.rank : '-',
    pendingCount: dashboard?.pendingCount || 0,
    processedCount: dashboard?.processedCount || 0,
  };

  return (
    <div className="bts-app">
      <div className="bts-app__content">
        <HeaderHero />

        <div className="bts-app__summary-wrap">
          {dashLoading ? (
            <div className="bts-app__loading-card">กำลังโหลด...</div>
          ) : (
            <SummaryCard user={user} onScanOpen={() => setScanOpen(true)} />
          )}
        </div>

        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        <div key={activeTab} className="bts-tab-content">
          {activeTab === 'missions' && <MissionsPage onTabChange={setActiveTab} onScanOpen={() => setScanOpen(true)} hasParty={!!dashboard?.party} />}
          {activeTab === 'team' && <TeamPage showPopup={showPopup} />}
          {activeTab === 'history' && <HistoryPage showPopup={showPopup} />}
          {activeTab === 'ranking' && <RankingPage showPopup={showPopup} />}
        </div>

        {/* {activeTab === 'missions' && <FixedBottomCTA onClick={() => setScanOpen(true)} />} */}

        <div className="bts-app__spacer" />
      </div>

      {scanOpen && <ScanPage onClose={handleScanClose} showPopup={showPopup} />}

      {showHowTo && <BtsHowToPlay onAccept={handleAcceptHowTo} />}

      {joiningParty && (
        <div className="bts-app__joining-overlay">
          <div className="bts-app__joining-card">
            <span>⏳</span>
            <p>กำลังเข้าร่วมทีม...</p>
          </div>
        </div>
      )}

      {popup && (
        <BtsPopup
          title={popup.title}
          message={popup.message}
          type={popup.type}
          actions={popup.actions}
          onClose={() => setPopup(null)}
        />
      )}

      {campaignEnded && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: '20px 24px 24px',
            maxWidth: 340, width: '100%', textAlign: 'center',
            border: '3px solid #10C851', boxShadow: '4px 4px 0 #10C851',
          }}>
            <img
              src={closedLogo}
              alt="Big C Back to School"
              style={{
                width: '60%',
                maxWidth: 200,
                aspectRatio: '1 / 1',
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto 12px',
              }}
            />
            <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 12px', color: '#111', lineHeight: 1.4 }}>
              บิลรู้ใจคุณ เปิดเทอมใหญ่ หัวใจว้าวุ่น จบแล้ว!
            </h2>
            <p style={{ fontSize: 14, color: '#444', margin: '0 0 8px', lineHeight: 1.6 }}>
              ขอบคุณทุกคนที่ร่วมสนุกกับเกมบิ๊กซี 😊
            </p>
            <p style={{ fontSize: 13, color: '#666', margin: '0 0 10px', lineHeight: 1.6 }}>
              ทางเราจะแจ้งผลรางวัล 5 ทีมที่ชนะ อีกครั้งเร็วๆ นี้
            </p>
            <p style={{ fontSize: 16, color: '#10C851', fontWeight: 700, margin: 0, lineHeight: 1.5 }}>
              รอลุ้น รางวัล "กินไก่ฟรีทั้งปี" กันได้เลย! 🐔🏆
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameBtsApp;
